import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "../../../components/InputField/InputField";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import { getScreenDimensions, handleInputWithoutSpaces } from "../../../utils/helper";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { messages, patterns } from "../../../utils/constants";
import "./StudentForgotPassword.scss";
import ConfirmationPopup from "../../../components/ConfirmationPopup/ConfirmationPopup";
import images from "../../../assets/images";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { FORGOT_PASSWORD_STUDENT } from "../api/AdminLoginQuery";
import ToasterWithMessage from "../../../components/toaster";
import { useAppSelector } from "../../../redux/app/hooks";
import { ButtonClickEvents } from "../../../firebase/constants/events";
import { trackButtonClick } from "../../../firebase/analyticsHelper";

interface IFormInput {
  email: string;
}

//validation
const schema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required(messages?.email)
    .matches(patterns.email, messages.validEmail)
    .email(messages?.validEmail),
});

interface ForgotMailInput {
  emailId: string;
}

interface ForgotPasswordVariables {
  input: ForgotMailInput;
}

interface ForgotResponse {
  sendForgotPasswordMail: string;
}

const StudentForgotPassword = () => {
  const methods = useForm<IFormInput>({ resolver: yupResolver(schema) });
  const { handleSubmit, setFocus } = methods;
  const { messageGif } = images;
  const [openPopup, setPopup] = useState<boolean>(false);
  const navigate = useNavigate();
  const { error } = ToasterWithMessage();
  const isKaton = useAppSelector((state) => state.auth.isKaton);
  const { isMobileScreen } = getScreenDimensions();

  const [forgotPassword] = useMutation<ForgotResponse, ForgotPasswordVariables>(
    FORGOT_PASSWORD_STUDENT,
    {
      onCompleted: (data) => {
        if(data.sendForgotPasswordMail){
        setPopup(true)
        }
      },
      onError: (err) => {
        error(err.message);
      },
    }
  );

  const onSubmit = (data: IFormInput) => {
    trackButtonClick(ButtonClickEvents?.FORGOT_PASSWORD_OK_BUTTON_CLICK, { 
      description: "User clicked the submit button on forgot password form" 
    });

    forgotPassword({
      variables: {
        input: {
          emailId: data.email,
        },
      },
    });
  };

  const onOkButtonClick = () => {
    setPopup(false);
    navigate(isKaton === "virtualSchool" ? "/virtual-school/virtual-student-login" : "/student-login");
  };

  const onClickClose = () => {
    setPopup(false);
  }

  useEffect(()=>{
   setFocus('email')
  },[setFocus])

  return (
    <FormProvider {...methods}>
      <div className="forgotpasswordMaincontainer">
        <form
          autoComplete="off"
          className="classCodeForm"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <div className="inputFieldContainer">
            <div className="labelStyle">
              <span className="title1">Forgot password?</span>
              <span className="title2">{isMobileScreen ? "Enter your email to receive password" : "Please enter your email"}</span>
              <span className="title3"> to receive the password</span>
            </div>
            <InputField
              label="Email"
              name="email"
              type="text"
              onInput={handleInputWithoutSpaces}
            />
          </div>
          <div className={`submitButtonContainer ptaSubmitButtonContainer`}>
            <ButtonStyle
              disableButton={false}
              text="Ok"
              type="submit"
              width="154px"
            />
          </div>
        </form>
      </div>
      <ConfirmationPopup
        open={openPopup}
        message={isMobileScreen ? `Password has been sent to your email` : `Password is sent to your email` }
        gifData={messageGif}
        yesButtonFunctionality={onOkButtonClick}
        noButtonFunctionality={onClickClose}
        messageWidth={isMobileScreen ? "215px" : "195px"}
        page="forgotpassword"
      />
    </FormProvider>
  );
};

export default StudentForgotPassword;
